import { X } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { RuleConditionInputOperator } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import {
  defaultRuleValue,
  isListOperator,
  operatorsFor,
  type RuleFieldDef,
  type RuleValueKind,
} from '../schemas/rule-fields';
import type { RulesFormHost } from '../schemas/rules-form-schema';

import { RuleValueInput } from './rule-value-input';

export interface RuleConditionRowProps {
  index: number;
  fields: readonly RuleFieldDef[];
  kindOf: (field: string) => RuleValueKind;
  currency: string;
  canRemove: boolean;
  onRemove: () => void;
  /** The host form is read-only: field, operator and value are locked. */
  disabled?: boolean;
}

/** Field → operator → value. Changing the field or the operator's shape resets the value. */
export function RuleConditionRow({
  index,
  fields,
  kindOf,
  currency,
  canRemove,
  onRemove,
  disabled = false,
}: RuleConditionRowProps) {
  const { t } = useTranslation('productRules');
  const form = useFormContext<RulesFormHost>();
  const base = `rules.conditions.${index}` as const;
  const fieldId = form.watch(`${base}.field`);
  const operator = form.watch(`${base}.operator`);
  const def = fields.find((f) => f.id === fieldId);
  const kind = kindOf(fieldId);
  const operators = operatorsFor(kind);
  const productFields = fields.filter((f) => f.group === 'product');
  const attributeFields = fields.filter((f) => f.group === 'attribute');

  const changeField = (next: string) => {
    const nextKind = kindOf(next);
    const allowed = operatorsFor(nextKind);
    const nextOperator = allowed.includes(operator) ? operator : (allowed[0] ?? 'equals');
    form.setValue(`${base}.field`, next, { shouldDirty: true });
    form.setValue(`${base}.operator`, nextOperator, { shouldDirty: true });
    form.setValue(`${base}.value`, defaultRuleValue(nextKind, nextOperator), {
      shouldDirty: true,
    });
    form.clearErrors(`${base}.value`);
  };

  const changeOperator = (next: RuleConditionInputOperator) => {
    form.setValue(`${base}.operator`, next, { shouldDirty: true });
    if (isListOperator(next) !== isListOperator(operator)) {
      form.setValue(`${base}.value`, defaultRuleValue(kind, next), { shouldDirty: true });
    }
  };

  return (
    <div
      role="group"
      aria-label={t('rules.condition', { index: index + 1 })}
      className="grid gap-2 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto] md:items-start"
    >
      <FormField
        control={form.control}
        name={`${base}.field`}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className="sr-only">{t('rules.field')}</FormLabel>
            <Select value={field.value} onValueChange={changeField} disabled={disabled}>
              <FormControl>
                <SelectTrigger
                  className="w-full"
                  aria-label={t('rules.field')}
                  aria-invalid={fieldState.invalid}
                >
                  <SelectValue placeholder={t('rules.pickField')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('rules.groups.product')}</SelectLabel>
                  {productFields.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
                {attributeFields.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>{t('rules.groups.attribute')}</SelectLabel>
                    {attributeFields.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`${base}.operator`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="sr-only">{t('rules.operator')}</FormLabel>
            <Select
              value={field.value}
              onValueChange={(v) => changeOperator(v as RuleConditionInputOperator)}
              disabled={disabled || !fieldId}
            >
              <FormControl>
                <SelectTrigger className="w-full" aria-label={t('rules.operator')}>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {operators.map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`rules.operators.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`${base}.value`}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className="sr-only">{t('rules.value')}</FormLabel>
            <FormControl>
              <RuleValueInput
                field={def}
                operator={operator}
                value={field.value}
                onChange={field.onChange}
                currency={currency}
                aria-invalid={fieldState.invalid}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={!canRemove}
        aria-label={t('rules.removeCondition', { index: index + 1 })}
      >
        <X aria-hidden />
      </Button>
    </div>
  );
}
